import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Button,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TablePagination,
  Alert,
  Snackbar,
  Tabs,
  Tab,
  Chip,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
  Add as AddIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  GetApp as ExportIcon,
  Lightbulb as LightbulbIcon,
} from '@mui/icons-material';
import { Typography, PageHeader } from '@/components/ui';
import type { PageHeaderAction } from '@/components/ui/PageHeader';
import { TeamUserForm, TeamUserTable, RolesManagement } from '../../components';
import { CreateRolePage } from '../../pages';
import type { TeamUser, CreateTeamUserRequest, TeamUserRole, TeamUserListParams, TeamUserTableData } from '../../types';
import { TEAM_USER_ROLES } from '../../constants';
import { teamUserService } from '@/services/teamUserService';
import { useCommonBreadcrumbs } from '@/utils/breadcrumbs';
import { getBreadcrumbConfig } from '@/config/breadcrumbs';
import { toast } from 'react-hot-toast';

export const TeamsPage: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0); // 0 = Team members, 1 = Roles management
  const [showCreateRolePage, setShowCreateRolePage] = useState(false);
  const [teamUsers, setTeamUsers] = useState<TeamUser[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState<TeamUserRole | ''>('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [rolesRefreshTrigger, setRolesRefreshTrigger] = useState(0);
  const [editingUser, setEditingUser] = useState<TeamUserTableData | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [transactionSearchTerm, setTransactionSearchTerm] = useState('');
  const [merchantTransactionSearchTerm, setMerchantTransactionSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState("name"); // default
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({
    open: false,
    message: '',
    severity: 'info',
  });

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const loadTeamUsers = async () => {
    setIsLoading(true);
    try {

      // const params: TeamUserListParams = {
      //   page: currentPage,
      //   limit: rowsPerPage,
      //   role: roleFilter || undefined,
      //   search: searchTerm || undefined,
      // };
      const currentPage = page + 1; // Convert 0-based to 1-based page

      const response = await teamUserService.getTeamUsers(currentPage, rowsPerPage);
      setTeamUsers(response.results || []);
      setTotalCount(response.count || 0);
    } catch (error) {
      console.error('Error loading team users:', error);
      setTeamUsers([]);
      setTotalCount(0);
      toast.error('Failed to load team users', {
        style: { background: '#d32f2f', color: '#fff' },
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Load team users
  useEffect(() => {
    loadTeamUsers();
  }, [page, rowsPerPage]);

  // const showSnackbar = (message: string, severity: 'success' | 'error' | 'info' | 'warning') => {
  //   setSnackbar({
  //     open: true,
  //     message,
  //     severity,
  //   });
  // };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };


  const handleRoleFilterChange = (role: TeamUserRole | '') => {
    setRoleFilter(role);
    setPage(0);
    loadTeamUsers();
  };

  const onClearFilters = () => {
    setSearchTerm('');
    setRoleFilter('');
    setPage(0);
    loadTeamUsers();
    setStatusFilter('name');
  };

  const handleAddUser = () => {
    setEditingUser(null);
    setIsFormOpen(true);
  };

  const handleEditUser = (teamUser: TeamUserTableData) => {
    setEditingUser(teamUser);
    setIsFormOpen(true);
  };

  const handleDeleteUser = async (teamUser: TeamUserTableData) => {
    if (!teamUser.uuid) return;

    try {
      await teamUserService.deleteTeamUser(teamUser.uuid.toString());
      toast.success('Team user deleted successfully', {
        style: { background: '#43a047', color: '#fff' },
      });
      loadTeamUsers();
    } catch (error) {
      console.error('Error deleting team user:', error);
      toast.success('Failed to delete team user', {
        style: { background: '#43a047', color: '#fff' },
      });
    }
  };

  const handleFormSubmit = async (data: CreateTeamUserRequest) => {
    setIsSubmitting(true);
    try {
      if (editingUser) {
        // Update existing user
        await teamUserService.updateTeamUser(editingUser.uuid, data);
        toast.success('Team user updated successfully', {
          style: { background: '#43a047', color: '#fff' },
        });

      } else {
        // Create new user
        await teamUserService.createTeamUser(data);
        toast.success('Team user created successfully', {
          style: { background: '#43a047', color: '#fff' },
        });
      }

      setIsFormOpen(false);
      // setEditingUser(null);
      loadTeamUsers();
    } catch (error) {
      console.error('Error saving team user:', error);
      toast.success('Failed to save team user', {
        style: { background: '#d32f2f', color: '#fff' },
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFormClose = () => {
    setIsFormOpen(false);


  };

  // Pagination handlers
  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
    // useEffect will automatically fetch data when page changes
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    // useEffect will automatically fetch data when rowsPerPage changes
  };

  // Breadcrumbs
  const breadcrumbConfig = getBreadcrumbConfig('/teams');
  const breadcrumbs = useCommonBreadcrumbs({
    hideBreadcrumbs: breadcrumbConfig.hideBreadcrumbs,
    customBreadcrumbs: breadcrumbConfig.hideBreadcrumbs ? [] : undefined,
  });

  const finalBreadcrumbs = breadcrumbs.length > 0
    ? breadcrumbs
    : [
      {
        label: 'Dashboard',
        href: '/merchant/dashboard',
        onClick: () => navigate('/merchant/dashboard'),
      },
      {
        label: 'Teams',
        active: true,
      },
    ];

  // Subtitle generator
  const getSubtitle = () => {
    const parts: string[] = [];

    if (roleFilter) {
      parts.push(`Role: ${TEAM_USER_ROLES[roleFilter]}`);
    }

    if (searchTerm) {
      parts.push(`Search: "${searchTerm}"`);
    }

    const total = totalCount;
    const base = 'Manage team users and their roles';

    return parts.length > 0
      ? `${base} • ${parts.join(' • ')} (${total} ${total === 1 ? 'user' : 'users'})`
      : `${base} (${total} ${total === 1 ? 'user' : 'users'})`;
  };

  // Header Actions
  const headerActions: PageHeaderAction[] = [
    {
      label: 'Add User',
      icon: <AddIcon sx={{ fontSize: 16, color: '#fff' }} />,
      onClick: handleAddUser,
      variant: 'contained',
      size: 'medium',
    },
  ];

  // Individual search term handlers
  const handleTransactionSearchChange = (value: string) => {
    setTransactionSearchTerm(value);
  };

  const handleMerchantTransactionSearchChange = (value: string) => {
    setMerchantTransactionSearchTerm(value);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      onSearchTermChange();
    }
  };

  // Search handler: fetch transactions by search terms
  const onSearchTermChange = async () => {
    const transactionId = transactionSearchTerm.trim();
    const merchantId = merchantTransactionSearchTerm.trim();

    if (transactionId || merchantId) {
      try {
        const response = await teamUserService.getTeanUserData(
          transactionId || undefined,
          merchantId || undefined
        );

        setTeamUsers(response.results || []);
        setTotalCount(response.count || 0);
        // Update state - this will trigger re-render
        setSearchTerm(transactionId || merchantId);
      } catch (error) {
        setTeamUsers([]);
        console.error('Fetch error:', error);
      }
    } else {
      setTeamUsers([]);
      setSearchTerm('');
    }
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    // Refresh roles list when switching to roles tab
    if (newValue === 1) {
      setRolesRefreshTrigger((prev) => prev + 1);
    }
  };

  // If showing create role page, render it instead
  if (showCreateRolePage) {
    return (
      <CreateRolePage 
        onBack={() => {
          setShowCreateRolePage(false);
          setRolesRefreshTrigger((prev) => prev + 1);
        }}
      />
    );
  }

  return (
    <Box>
      <PageHeader
        title="Team and roles"
        //subtitle={getSubtitle()}
        //breadcrumbs={finalBreadcrumbs}
        actions={activeTab === 0 ? headerActions : []}
        showDivider={false}
      />

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={handleTabChange}>
          <Tab label="Team members" />
          <Tab label="Roles management" />
        </Tabs>
      </Box>

      {/* Team Members Tab Content */}
      {activeTab === 0 && (
        <>
          {/* Team Management Info */}
          {/* <Box sx={{ mb: 3, display: 'flex', alignItems: 'flex-start', gap: 1 }}>
            <LightbulbIcon sx={{ color: 'text.secondary', mt: 0.5 }} />
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                Team management
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Manage your team members who belong to this account with ease. View a comprehensive list of all members and their roles.
              </Typography>
            </Box>
          </Box> */}

          {/* Search and Filter Controls */}
      <Box
        mb={3}
        display="flex"
        flexDirection={{ xs: 'column', md: 'row' }}
        gap={{ xs: 2, md: 2 }}
        alignItems={{ xs: 'stretch', md: 'center' }}
        justifyContent="space-between"
        sx={{
          '& .MuiOutlinedInput-root': {
            borderRadius: '8px',
          },
        }}
      >
        <FormControl
          variant="outlined"
          size="small"
          sx={{
            minWidth: { xs: '100%', sm: 180 },
            '& .MuiOutlinedInput-root': {
              background: 'white',
            },
          }}
        >
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <MenuItem value="name">Name</MenuItem>
            <MenuItem value="email">Email</MenuItem>
          </Select>
        </FormControl>

        <Box display="flex"
          flexDirection={{ xs: 'column', sm: 'row' }}
          gap={{ xs: 2, sm: 2 }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          flex={1}>

          {statusFilter === "name" &&
            <TextField
              variant="outlined"
              type="search"
              placeholder="Search"
              value={transactionSearchTerm}
              onChange={(e) => handleTransactionSearchChange(e.target.value)}
              onKeyDown={handleKeyDown}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" sx={{ fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                flex: 1,
                maxWidth: { xs: '100%', sm: 300, md: 200 },
                '& .MuiOutlinedInput-root': {
                  background: 'white',
                },
              }}
            />
          }
          {statusFilter === "email" &&

            <TextField
              variant="outlined"
              type="search"
              placeholder="Search"
              value={merchantTransactionSearchTerm}
              onChange={(e) => handleMerchantTransactionSearchChange(e.target.value)}
              onKeyDown={handleKeyDown}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" sx={{ fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                flex: 1,
                maxWidth: { xs: '100%', sm: 300, md: 200 },
                '& .MuiOutlinedInput-root': {
                  background: 'white',
                },
              }}
            />
          }
          {/* Clear Filters Button */}
          <Button
            size="small"
            onClick={onClearFilters}
            sx={{ minWidth: { xs: '100%', sm: 'auto' } }}
          >
            Clear Filters
          </Button>
        </Box>

        {/* Filter Controls */}
        <Box display="flex"
          flexDirection={{ xs: 'column', sm: 'row' }}
          gap={{ xs: 2, sm: 2 }}
          alignItems={{ xs: 'stretch', sm: 'center' }}>
          {/* Role Filter */}
          {/* <FormControl
            variant="outlined"
            size="small"
            sx={{
              minWidth: 180,
              '& .MuiOutlinedInput-root': {
                background: 'white',
              },
            }}
          >
            <InputLabel>Role</InputLabel>
            <Select
              value={roleFilter}
              onChange={(e) => handleRoleFilterChange(e.target.value as TeamUserRole | '')}
              label="Role"
            >
              <MenuItem value="">All Roles</MenuItem>
              {Object.entries(TEAM_USER_ROLES).map(([key, value]) => (
                <MenuItem key={key} value={key}>
                  {value}
                </MenuItem>
              ))}
            </Select>
          </FormControl> */}

        </Box>
      </Box>

          {/* Team Users Table */}
          <TeamUserTable
            teamUsers={teamUsers}
            onEdit={handleEditUser}
            onDelete={handleDeleteUser}
            isLoading={isLoading}
          />

          {/* Pagination */}
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Rows per page:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`}
          />

          {/* Team User Form Dialog */}
          <TeamUserForm
            open={isFormOpen}
            onClose={handleFormClose}
            onSubmit={handleFormSubmit}
            teamUser={editingUser}
            isLoading={isSubmitting}
          />
        </>
      )}

      {/* Roles Management Tab Content */}
      {activeTab === 1 && (
        <>
          {/* Permissions Comparisons Info */}
          {/* <Box sx={{ mb: 3, display: 'flex', alignItems: 'flex-start', gap: 1 }}>
            <LightbulbIcon sx={{ color: 'text.secondary', mt: 0.5 }} />
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                Permissions comparisons
              </Typography>
              <Typography variant="body2" color="textSecondary">
                The table below displays all available roles for your organization, including default and custom roles that can be selected for team members.
              </Typography>
            </Box>
          </Box> */}

          {/* Create Role Button */}
          <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              startIcon={<AddIcon sx={{color:'#fff'}} />}
              onClick={() => setShowCreateRolePage(true)}
              sx={{
                backgroundColor: '#17A6DA',
                '&:hover': {
                  backgroundColor: '#17A6DA',
                },
              }}
            >
              Create role
            </Button>
          </Box>

          {/* Roles Management Table */}
          <RolesManagement 
            onCreateRole={() => setShowCreateRolePage(true)} 
            refreshTrigger={rolesRefreshTrigger}
          />
        </>
      )}

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TeamsPage;
